import React, { lazy, Suspense } from 'react';
import { RouteProps } from '@/models/routes';

import Login from '@/pages/Auth/login/login';
import LoginSms from '@/pages/Auth/login-sms/login-sms';
import ForgotPassword from '@/pages/Auth/ForgotPassword';
import ResetPassWord from '@/pages/Auth/ResetPassword';
import LinkExpired from '@/pages/Auth/LinkExpired';
import NonAuthedContainer from '@/components/core/non-authed-container/non-authed-container';
import AuthedContainer from '@/components/core/authed-container/authed-container';
import ResetPass from '@/pages/Auth/reset-pass/reset-pass';
import TosAndPP from '@/pages/Auth/TosAndPP';
import { PERMISSIONS } from '@/constants/permissions';

const Layout = lazy(() => lazyRetry(() => import('@/layout/layout')));
const EditPatient = lazy(() =>
  lazyRetry(() => import('@/pages/edit-patient/edit-patient'))
);
const Home = lazy(() => lazyRetry(() => import('@/pages/home/home')));
const Admin = lazy(() => lazyRetry(() => import('@/pages/admin/admin')));
const EditAdmin = lazy(() => lazyRetry(() => import('@/pages/admin/Edit')));

const CreatePatient = lazy(() =>
  lazyRetry(() => import('@/pages/create-patient/create-patient'))
);
const AddThreshold = lazy(() =>
  lazyRetry(() => import('@/pages/add-threshold/add-threshold'))
);
const EditThreshold = lazy(() =>
  lazyRetry(() => import('@/pages/edit-threshold/edit-threshold'))
);
const PatientList = lazy(() =>
  lazyRetry(() => import('@/pages/patient-list/patient-list'))
);
const PatientDetail = lazy(() =>
  lazyRetry(() => import('@/pages/patient-detail/patient-detail'))
);
const PatientAlarmOverview = lazy(() =>
  lazyRetry(
    () => import('@/pages/patient-alarm-overview/patient-alarm-overview')
  )
);
const Profile = lazy(() => lazyRetry(() => import('@/pages/profile')));
const TransferPatient = lazy(() =>
  lazyRetry(() => import('@/pages/transfer-patient/transfer-patient'))
);
const NewPatient = lazy(() =>
  lazyRetry(() => import('@/pages/new-patient/new-patient'))
);
const NewPatientDetail = lazy(() =>
  lazyRetry(() => import('@/pages/new-patient/Detail'))
);

const routes: RouteProps[] = [
  {
    path: '/',
    element: authedLazyLoad(Layout),
    children: [
      {
        path: '/',
        element: authedLazyLoad(Home),
        children: [
          {
            index: true,
            path: '/',
            element: authedLazyLoad(PatientList),
          },
          {
            path: 'patient-detail/:id',
            element: authedLazyLoad(PatientDetail),
          },
        ],
      },
      {
        path: 'create-patient',
        element: authedLazyLoad(CreatePatient, [PERMISSIONS.PatientCreate]),
      },
      {
        path: 'transfer-patient/:id',
        element: authedLazyLoad(TransferPatient, [PERMISSIONS.PatientCreate]),
      },
      {
        path: 'profile',
        element: authedLazyLoad(Profile),
      },
      {
        children: [
          {
            index: true,
            path: 'new-patient',
            element: authedLazyLoad(NewPatient, [
              PERMISSIONS.PatientTransferPendingView,
            ]),
          },
          {
            path: 'new-patient-detail/:id',
            element: authedLazyLoad(NewPatientDetail, [
              PERMISSIONS.PatientTransferPendingView,
            ]),
          },
        ],
      },
      {
        children: [
          {
            index: true,
            path: 'admin',
            element: authedLazyLoad(Admin, [PERMISSIONS.UserAdmin]),
          },
          {
            path: 'admin-edit/:id',
            element: authedLazyLoad(EditAdmin, [PERMISSIONS.UserAdmin]),
          },
          {
            path: 'admin-create',
            element: authedLazyLoad(EditAdmin, [PERMISSIONS.UserAdmin]),
          },
        ],
      },
      {
        path: 'edit-patient/:id',
        element: authedLazyLoad(EditPatient, [PERMISSIONS.PatientCreate]),
      },
      {
        path: 'add-threshold/:id',
        element: authedLazyLoad(AddThreshold, [PERMISSIONS.PatientCreate]),
      },
      {
        // Batch 009 / ADR-3: threshold row ids left the contract — the second
        // parameter is now the TYPE key (a loinc; 8480-6 stands for the BP
        // visual pair).
        path: 'edit-threshold/:id/:loinc',
        element: authedLazyLoad(EditThreshold, [PERMISSIONS.PatientCreate]),
      },
    ],
  },

  {
    path: 'patient-detail/:id/alarm-overview',
    element: authedLazyLoad(PatientAlarmOverview),
  },
  {
    path: 'login',
    element: (
      <NonAuthedContainer>
        <Login />
      </NonAuthedContainer>
    ),
  },
  {
    path: 'forgot-password',
    element: (
      <NonAuthedContainer>
        <ForgotPassword />
      </NonAuthedContainer>
    ),
  },
  {
    path: 'reset-password',
    element: (
      <NonAuthedContainer>
        <ResetPassWord />
      </NonAuthedContainer>
    ),
  },
  {
    path: 'auth/link-expired',
    element: (
      <NonAuthedContainer>
        <LinkExpired />
      </NonAuthedContainer>
    ),
  },
  {
    path: 'login-sms',
    element: (
      <NonAuthedContainer>
        <LoginSms />
      </NonAuthedContainer>
    ),
  },
  {
    path: 'tos-and-pp',
    element: (
      <NonAuthedContainer>
        <TosAndPP />
      </NonAuthedContainer>
    ),
  },
  {
    path: 'reset-pass',
    children: [
      {
        index: true,
        path: '',
        element: (
          <NonAuthedContainer>
            <ResetPass />
          </NonAuthedContainer>
        ),
      },
      {
        path: ':isPwdExpired',
        element: (
          <NonAuthedContainer>
            <ResetPass />
          </NonAuthedContainer>
        ),
      },
    ],
  },
];

function authedLazyLoad(
  Comp: React.LazyExoticComponent<any>,
  requiredPermission?: string[]
): React.ReactNode {
  return (
    <Suspense fallback={<></>}>
      <AuthedContainer requiredPermissions={requiredPermission}>
        <Comp />
      </AuthedContainer>
    </Suspense>
  );
}

const lazyRetry = (componentImport: any): Promise<any> =>
  new Promise(resolve => {
    componentImport()
      .then((component: any) => {
        resolve(component);
      })
      .catch(() => null);
  });

export default routes;
